// This file includes helper functions used inside the payment plan configuration under
// apps/admin/src/App/Account/Content/Patients/Content/Content/Sections/ActiveTab/TreatmentCard/CreateTreatmentPlan/EditTreatmentAndPaymentPlans/New/Steps/PaymentPlan

import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  compareAsc,
  differenceInCalendarMonths,
  endOfMonth,
  endOfYear,
  formatISO,
  getYear,
  isAfter,
  isEqual,
  max,
  min,
  parseISO,
  setDate,
  setYear,
  startOfMonth,
} from 'date-fns'
import BigNumber from 'bignumber.js'

export let INCREMENT_STEP = 5
export let DEFAULT_TREATMENT_PLAN_LENGTH_IN_WEEKS = 4

export let LOCK = /** @type {const} */ ({
  INSTALLMENT_AMOUNT: 'INSTALLMENT_AMOUNT',
  DOWNPAYMENT_AMOUNT: 'DOWNPAYMENT_AMOUNT',
  PAYMENT_PLAN_LENGTH: 'PAYMENT_PLAN_LENGTH',
})

export let INTERVAL = /** @type {const} */ ({
  WEEKLY: 'Weekly',
  EVERY_SECOND_WEEK: 'EverySecondWeek',
  TWICE_PER_MONTH: 'TwicePerMonth',
  MONTHLY: 'Monthly',
})

export let INSURANCE_INSTALLMENT_INTERVAL = /** @type {const} */ ({
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  YEARLY: 'YEARLY',
})

export let COORDINATION_OF_BENEFITS_TYPE = /** @type {const} */ ({
  STANDARD: 'Standard',
  NON_DUPLICATING: 'NonDuplicating',
})

export let DOWNPAYMENT_TYPE = /** @type {const} */ ({
  FIXED: 'FIXED',
  PERCENTAGE: 'PERCENTAGE',
})

export let DOWNPAYMENT_PERCENTAGE_BASE_TYPE = /** @type {const} */ ({
  TX_FEE: 'TX_FEE',
  LIFETIME_COVERAGE: 'LIFETIME_COVERAGE',
})

export let AGE_LIMIT_CUTOFF = /** @type {const} */ ({
  IMMEDIATE: 'Immediate',
  END_OF_MONTH: 'EndOfMonth',
  END_OF_YEAR: 'EndOfYear',
})

export let DEFAULT_PAYOR_SCHEDULE_TYPE = /** @type {const} */ ({
  INSTALLMENT_AMOUNT: 'INSTALLMENT_AMOUNT',
  PAYMENT_PLAN_LENGTH: 'PAYMENT_PLAN_LENGTH',
})

export let PAYMENT_PLAN_LENGTH_TYPE = /** @type {const} */ ({
  /** Fixed payment plan length */
  TOTAL: 'TOTAL',
  /** Payment plan length calculated relative to treatment plan length (value could be negative) */
  RELATIVE: 'RELATIVE',
})

export let DISCOUNT_TYPE = /** @type {const} */ ({
  /** Fixed discount amount */
  FIXED: 'FIXED',
  /** Discount amount calculated as a percentage of the treatment fee or payor's share (depending on what it applies to) */
  PERCENTAGE: 'PERCENTAGE',
})

/** @type {(value: string) => boolean} */
export function isWeekly(value) {
  return INTERVAL.WEEKLY === value || INTERVAL.EVERY_SECOND_WEEK === value
}

/**
 * Returns the number of installments in a month depending on the chosen interval
 * @type {(installment_interval: typeof INTERVAL[keyof typeof INTERVAL]) => number}
 */
export function getNumberOfInstallmentsPerMonth(installment_interval) {
  switch (installment_interval) {
    case INTERVAL.WEEKLY:
      return 4
    case INTERVAL.EVERY_SECOND_WEEK:
    case INTERVAL.TWICE_PER_MONTH:
      return 2
    case INTERVAL.MONTHLY:
    default:
      return 1
  }
}

/**
 * Returns the total treatment fee after applying the discounts and charges.
 *
 * Note: only the discounts applied on treatment fee are taken into account at this stage.
 *
 * @type {(payment_plan: import('./payment-plan-types.d.ts').PaymentPlan) => number}
 */
export function getAdjustedTreatmentFeeBeforeInsurances(payment_plan) {
  // discounts could be either 'fixed amount' or 'percentage', but the values should always be in sync
  let discounts = payment_plan.discounts
    .filter(discount => !discount.is_applied_to_payor)
    .reduce(
      (acc, discount) => acc.minus(discount.amount),
      BigNumber(payment_plan.treatment_fee)
    )

  let result = payment_plan.charges
    .reduce((acc, charge) => acc.plus(charge.amount), discounts)
    .decimalPlaces(2)
    .toNumber()

  return Math.max(result, 0)
}

/**
 * Returns the initial configuration for the payor (i.e. pay in full 100% of the remaining fee)
 *
 * @type {(
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  settings: import('./payment-plan-types.d.ts').Settings
 * ) => import('./payment-plan-types.d.ts').Payor}
 */
export function getDefaultPrimaryPayor(payment_plan, treatment_plan, settings) {
  let treatment_fee = getAdjustedTreatmentFeeAfterInsurances(payment_plan)
  let downpayment_amount = getDefaultPayorDownpayment(treatment_fee, settings)
  let total_installments_amount = BigNumber(treatment_fee)
    .minus(downpayment_amount)
    .decimalPlaces(2)
    .toNumber()

  let { installment_amount, payment_plan_length } =
    getPayorDefaultInstallmentAmountAndPaymentPlanLength(
      total_installments_amount,
      treatment_plan,
      settings
    )
  let first_installment_date =
    settings.default_payor_schedule.allowed_dates?.[0] ?? 1

  return {
    id: crypto.randomUUID(),
    person_id: null,
    is_primary: true,
    share: {
      type: 'percentage',
      amount: treatment_fee,
      percentage: 100,
    },
    downpayment_amount,
    installment_amount,
    payment_plan_length,
    installment_interval: INTERVAL.MONTHLY,
    first_due_date: getFirstDueDate(first_installment_date),
    first_installment_date,
    second_installment_date: 1,
    lock: null,
  }
}

/**
 *
 * @type {(
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  settings: import('./payment-plan-types.d.ts').Settings
 * ) => import('./payment-plan-types.d.ts').Payor}
 */
export function getDefaultSecondaryPayor(
  payment_plan,
  treatment_plan,
  settings
) {
  let remaining_fee = getAdjustedTreatmentFeeAfterInsurances(payment_plan)
  let primary_payor = payment_plan.payors.find(payor => payor.is_primary)
  let share_amount = BigNumber(remaining_fee)
    .minus(primary_payor ? primary_payor.share.amount : 0)
    .decimalPlaces(2)
    .toNumber()

  let downpayment_amount = getDefaultPayorDownpayment(share_amount, settings)
  let { installment_amount, payment_plan_length } =
    getPayorDefaultInstallmentAmountAndPaymentPlanLength(
      BigNumber(share_amount)
        .minus(downpayment_amount)
        .decimalPlaces(2)
        .toNumber(),
      treatment_plan,
      settings
    )

  let first_installment_date =
    settings.default_payor_schedule.allowed_dates?.[0] ?? 1

  return {
    id: crypto.randomUUID(),
    person_id: null,
    is_primary: false,
    share: {
      type: 'percentage',
      amount: share_amount,
      percentage: 100 - (primary_payor ? primary_payor.share.percentage : 0),
    },
    downpayment_amount,
    installment_amount,
    payment_plan_length,
    installment_interval: INTERVAL.MONTHLY,
    first_due_date: getFirstDueDate(first_installment_date),
    first_installment_date,
    second_installment_date: 1,
    lock: null,
  }
}

/** @type {(installment_date: number) => string} */
export function getFirstDueDate(installment_date) {
  let value = setDate(addMonths(startOfMonth(new Date()), 1), installment_date)
  return formatISO(value, { representation: 'date' })
}

/**
 * Calculates the initial values of the installment amount and payment plan length of a newly added payor according to the settings.
 *
 * @type {(
 *  total_installments_amount: number,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  settings: import('./payment-plan-types.d.ts').Settings
 * ) => {
 *  installment_amount: number,
 *  payment_plan_length: number
 * }}
 */
function getPayorDefaultInstallmentAmountAndPaymentPlanLength(
  total_installments_amount,
  treatment_plan,
  settings
) {
  let installment_amount = 0
  let payment_plan_length = 0

  switch (settings.default_payor_schedule.type) {
    case DEFAULT_PAYOR_SCHEDULE_TYPE.PAYMENT_PLAN_LENGTH: {
      payment_plan_length = getPayorDefaultPaymentPlanLength(
        treatment_plan,
        settings
      )
      installment_amount =
        payment_plan_length > 0
          ? BigNumber(total_installments_amount)
              .dividedBy(payment_plan_length)
              .decimalPlaces(2)
              .toNumber()
          : total_installments_amount
      break
    }

    case DEFAULT_PAYOR_SCHEDULE_TYPE.INSTALLMENT_AMOUNT: {
      installment_amount = Math.min(
        settings.default_payor_schedule.installment_amount,
        total_installments_amount
      )
      // Note that the installment frequency is initially set to Monthly (number of installments equals to the number of months)
      payment_plan_length =
        installment_amount > 0
          ? Math.ceil(
              BigNumber(total_installments_amount)
                .dividedBy(installment_amount)
                .decimalPlaces(2)
                .toNumber()
            )
          : 0
      let maximum_payment_plan_length = getMaximumPaymentPlanLength(
        treatment_plan,
        settings
      )
      if (payment_plan_length > maximum_payment_plan_length) {
        payment_plan_length = maximum_payment_plan_length
        // rounding as ceil to try and keep the same length
        installment_amount = Math.ceil(
          BigNumber(total_installments_amount)
            .dividedBy(payment_plan_length)
            .decimalPlaces(2)
            .toNumber()
        )
      }
      break
    }

    default: {
      break
    }
  }

  return { installment_amount, payment_plan_length }
}

/**
 * Returns the default downpayment amount for given payor. Takes into account the settings.
 *
 * @type {(payor_share: number, settings: import('./payment-plan-types.d.ts').Settings) => number}
 */
function getDefaultPayorDownpayment(payor_share, settings) {
  let default_downpayment_amount = 0

  switch (settings.default_downpayment.type) {
    case DOWNPAYMENT_TYPE.FIXED: {
      default_downpayment_amount = settings.default_downpayment.amount
      break
    }

    case DOWNPAYMENT_TYPE.PERCENTAGE: {
      default_downpayment_amount = BigNumber(payor_share)
        .multipliedBy(settings.default_downpayment.percentage)
        .decimalPlaces(2)
        .toNumber()
      break
    }

    default: {
      default_downpayment_amount = 0
      break
    }
  }

  if (
    default_downpayment_amount > 0 &&
    payor_share > default_downpayment_amount
  ) {
    return default_downpayment_amount
  } else {
    return payor_share
  }
}

/**
 * Returns the minimum downpayment amount the given payor is allowed to pay according to the settings.
 *
 * @type {(payor_share: number, settings: import('./payment-plan-types.d.ts').Settings) => number}
 */
export function getPayorMinimumDownpaymentAmount(payor_share, settings) {
  let minimum_downpayment_amount = 0

  switch (settings.minimum_downpayment.type) {
    case DOWNPAYMENT_TYPE.FIXED: {
      minimum_downpayment_amount = settings.minimum_downpayment.amount
      break
    }

    case DOWNPAYMENT_TYPE.PERCENTAGE: {
      minimum_downpayment_amount = BigNumber(payor_share)
        .multipliedBy(settings.minimum_downpayment.percentage)
        .decimalPlaces(2)
        .toNumber()
      break
    }

    default: {
      break
    }
  }

  // prevent it from going over payor's total share
  return Math.min(minimum_downpayment_amount, payor_share)
}

/**
 * @type {(
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  settings: import('./payment-plan-types.d.ts').Settings
 * ) => number}
 */
export function getMaximumPaymentPlanLength(treatment_plan, settings) {
  switch (settings.maximum_payment_plan_length.type) {
    case PAYMENT_PLAN_LENGTH_TYPE.TOTAL: {
      return settings.maximum_payment_plan_length.value
    }
    case PAYMENT_PLAN_LENGTH_TYPE.RELATIVE: {
      return (
        weeksToMonths(treatment_plan.length_in_weeks) +
        settings.maximum_payment_plan_length.value
      )
    }
    default: {
      return 60
    }
  }
}

/**
 * @type {(
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  settings: import('./payment-plan-types.d.ts').Settings
 * ) => number}
 */
export function getPayorDefaultPaymentPlanLength(treatment_plan, settings) {
  if (
    settings.default_payor_schedule.type !==
    DEFAULT_PAYOR_SCHEDULE_TYPE.PAYMENT_PLAN_LENGTH
  ) {
    return 1
  }

  let result = 1
  switch (settings.default_payor_schedule.payment_plan_length.type) {
    case PAYMENT_PLAN_LENGTH_TYPE.TOTAL: {
      result = settings.default_payor_schedule.payment_plan_length.value
      break
    }
    case PAYMENT_PLAN_LENGTH_TYPE.RELATIVE: {
      result =
        weeksToMonths(treatment_plan.length_in_weeks) +
        settings.default_payor_schedule.payment_plan_length.value
      break
    }
    default: {
      break
    }
  }

  // ensure it won't go over the max payment plan length configured
  return Math.min(result, getMaximumPaymentPlanLength(treatment_plan, settings))
}

/** @type {(value: number) => number} */
export function weeksToMonths(value) {
  // To convert weeks to months we divide by 4.34524, that is how many weeks are in a month
  // 365 (days in a year) / 7 (days in a week) / 12 (months in a year) = 4.34524
  // Core performs this calculation
  return Math.round(value / 4.34524)
}

/**
 * Get payor's remaining amount to pay after applying their discounts.
 * @type {(
 *  payor: import('./payment-plan-types.d.ts').Payor,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan
 * ) => number}
 */
export function getPayorShareAfterDiscounts(payor, payment_plan) {
  return Math.max(
    BigNumber(payor.share.amount)
      .minus(getPayorDiscountsAmount(payor, payment_plan))
      .decimalPlaces(2)
      .toNumber(),
    0
  )
}

/**
 * @type {(
 *  payor: import('./payment-plan-types.d.ts').Payor,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan
 * ) => number}
 */
export function getPayorDiscountsAmount(payor, payment_plan) {
  return payment_plan.discounts
    .filter(discount => isDiscountAppliedToPayor(payor, discount))
    .reduce((acc, discount) => acc.plus(discount.amount), BigNumber(0))
    .decimalPlaces(2)
    .toNumber()
}

/**
 * Finds the payor that the discount is applied to, if the discount is applied to a payor's share.
 *
 * @type {(
 *  discount: import('./payment-plan-types.d.ts').Discount,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan
 * ) => import('./payment-plan-types.d.ts').Payor | undefined}
 */
export function getPayorForDiscount(discount, payment_plan) {
  return payment_plan.payors.find(payor =>
    isDiscountAppliedToPayor(payor, discount)
  )
}

/**
 * Returns true if the given discount is applied on payor's share
 * @type {(
 *  payor: import('./payment-plan-types.d.ts').Payor,
 *  discount: import('./payment-plan-types.d.ts').Discount
 * ) => boolean}
 */
export function isDiscountAppliedToPayor(payor, discount) {
  return (
    discount.is_applied_to_payor &&
    ((discount.payor_person_id &&
      discount.payor_person_id === payor.person_id) ||
      (!discount.payor_person_id && payor.is_primary))
  )
}

/** @type {(payment_plan: import('./payment-plan-types.d.ts').PaymentPlan) => number} */
export function getRemainingAmount(payment_plan) {
  let payors_share_amount = payment_plan.payors.reduce(
    (acc, payor) => acc.plus(payor.share.amount),
    BigNumber(0)
  )

  return BigNumber(getAdjustedTreatmentFeeAfterInsurances(payment_plan))
    .minus(payors_share_amount)
    .decimalPlaces(2)
    .toNumber()
}

/**
 * Recalculates payor's downpayment, installment amount and payment plan length when the payor's share has changed.
 *
 * @type {(
 *  payor: import('./payment-plan-types.d.ts').Payor,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan
 * ) => {
 *  downpayment_amount: number,
 *  installment_amount: number,
 *  payment_plan_length: number
 * }}
 */
export function getUpdatedPayorConfiguration(payor, payment_plan) {
  let discounts_amount = getPayorDiscountsAmount(payor, payment_plan)
  let remaining_amount = Math.max(
    BigNumber(payor.share.amount)
      .minus(discounts_amount)
      .decimalPlaces(2)
      .toNumber(),
    0
  )

  if (payor.installment_amount === 0 || payor.payment_plan_length === 0) {
    return {
      downpayment_amount: remaining_amount,
      installment_amount: 0,
      payment_plan_length: 0,
    }
  }

  // keep the same downpayment
  let downpayment_amount = Math.min(payor.downpayment_amount, remaining_amount)
  let installment_amount = 0
  let payment_plan_length = 0

  let total_installments_amount = BigNumber(remaining_amount)
    .minus(payor.downpayment_amount)
    .decimalPlaces(2)
    .toNumber()
  let number_of_installments_per_month = getNumberOfInstallmentsPerMonth(
    payor.installment_interval
  )
  let current_monthly_installment_amount = BigNumber(
    payor.installment_amount
  ).multipliedBy(number_of_installments_per_month)
  let lower_threshold = current_monthly_installment_amount
    .multipliedBy(payor.payment_plan_length - 1)
    .decimalPlaces(2)
    .toNumber()
  let upper_threshold = current_monthly_installment_amount
    .multipliedBy(payor.payment_plan_length)
    .decimalPlaces(2)
    .toNumber()

  if (
    total_installments_amount > lower_threshold &&
    total_installments_amount < upper_threshold &&
    payor.payment_plan_length > 1
  ) {
    // the updated amount is within bounds (no changes needed, the last installment amount will reflect the change)
    installment_amount = payor.installment_amount
    payment_plan_length = payor.payment_plan_length
  } else if (total_installments_amount === 0) {
    // less than the previous downpayment, set to pay in full
    installment_amount = 0
    payment_plan_length = 0
  } else if (payor.lock === LOCK.INSTALLMENT_AMOUNT) {
    // keep the same installment amount and only adjust the payment plan length
    installment_amount = payor.installment_amount
    payment_plan_length =
      installment_amount > 0
        ? Math.ceil(
            BigNumber(total_installments_amount)
              .dividedBy(payor.installment_amount)
              .multipliedBy(number_of_installments_per_month)
              .decimalPlaces(2)
              .toNumber()
          )
        : 0
  } else if (payor.payment_plan_length === 1) {
    installment_amount = total_installments_amount
    payment_plan_length = 1
  } else {
    let total_number_of_installments =
      payor.payment_plan_length * number_of_installments_per_month
    // rounding as ceil to try and keep the same length
    installment_amount = Math.ceil(
      BigNumber(total_installments_amount)
        .dividedBy(total_number_of_installments)
        .decimalPlaces(2)
        .toNumber()
    )
    // ensure rounding is not affecting the number of instalments
    payment_plan_length =
      installment_amount > 0
        ? Math.ceil(
            BigNumber(total_installments_amount)
              .dividedBy(installment_amount)
              .multipliedBy(number_of_installments_per_month)
              .decimalPlaces(2)
              .toNumber()
          )
        : 0
  }

  return { downpayment_amount, installment_amount, payment_plan_length }
}

/**
 * Calculate the last installment amount
 *
 * @type {(
 *  payor: import('./payment-plan-types.d.ts').Payor,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan
 * ) => number}
 */
export function getLastInstallmentAmount(payor, payment_plan) {
  if (payor.payment_plan_length === 0 || payor.installment_amount === 0) {
    return 0
  }

  let discounts_amount = getPayorDiscountsAmount(payor, payment_plan)
  let total_installments_amount = Math.max(
    BigNumber(payor.share.amount)
      .minus(discounts_amount)
      .minus(payor.downpayment_amount)
      .decimalPlaces(2)
      .toNumber(),
    0
  )
  if (total_installments_amount === 0) return 0

  let number_of_installments = Math.ceil(
    BigNumber(total_installments_amount)
      .dividedBy(payor.installment_amount)
      .decimalPlaces(2)
      .toNumber()
  )

  return BigNumber(total_installments_amount)
    .minus(
      BigNumber(payor.installment_amount).multipliedBy(
        number_of_installments - 1
      )
    )
    .decimalPlaces(2)
    .toNumber()
}

/**
 * Get the treatment fee used as the base for the calculation of the insurance coverage.
 *
 * @type {(payment_plan: import('./payment-plan-types.d.ts').PaymentPlan) => number}
 */
export function getInsuranceTreatmentFee(payment_plan) {
  let charges = payment_plan.charges
    .filter(charge => charge.is_included_in_insurance_claim)
    .reduce((acc, charge) => acc.plus(charge.amount), BigNumber(0))

  // discounts could be either 'fixed amount' or 'percentage', but the values should always be in sync
  let discounts = payment_plan.discounts
    // adding this filter for completeness,
    // discounts applied to payor's share should not be included in insurance claim (only treatment fee are relevant here)
    .filter(
      discount =>
        !discount.is_applied_to_payor && discount.is_included_in_insurance_claim
    )
    .reduce((acc, discount) => acc.plus(discount.amount), BigNumber(0))

  return BigNumber(payment_plan.treatment_fee)
    .plus(charges)
    .minus(discounts)
    .decimalPlaces(2)
    .toNumber()
}

/** @type {(payment_plan: import('./payment-plan-types.d.ts').PaymentPlan) => number} */
export function getAdjustedTreatmentFeeAfterInsurances(payment_plan) {
  let treatment_fee = getAdjustedTreatmentFeeBeforeInsurances(payment_plan)
  if (payment_plan.insurances.length === 0) return treatment_fee

  let estimated_reimbursement_amount = payment_plan.insurances.reduce(
    (acc, insurance) => acc.plus(insurance.estimated_reimbursement_amount),
    BigNumber(0)
  )
  return BigNumber(treatment_fee)
    .minus(estimated_reimbursement_amount)
    .decimalPlaces(2)
    .toNumber()
}

/**
 * @type {(
 *  insurance: import('./payment-plan-types.d.ts').Insurance,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan
 * ) => number}
 */
export function getTotalDeductibleAmount(insurance, treatment_plan) {
  let start_date = new Date()
  let end_date = addWeeks(
    start_date,
    treatment_plan.length_in_weeks ?? DEFAULT_TREATMENT_PLAN_LENGTH_IN_WEEKS
  )
  let ortho_insured = insurance.insured.tx_category_insured.ortho_insured
  let start_year = ortho_insured.year_deductible_paid_last
    ? Math.max(getYear(start_date), ortho_insured.year_deductible_paid_last)
    : getYear(start_date)
  let end_year = getYear(end_date)
  let calendar_years_spanned = end_year - start_year + 1

  return BigNumber(
    insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages.deductible_amount
  )
    .multipliedBy(calendar_years_spanned)
    .decimalPlaces(2)
    .toNumber()
}

/**
 * @type {(
 *  primary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan
 * ) => number}
 */
export function getPrimaryInsuranceMaximumReimbursementAmount(
  primary_insurance,
  payment_plan,
  treatment_plan
) {
  return getMaximumReimbursementAmount(
    primary_insurance,
    getInsuranceTreatmentFee(payment_plan),
    getTotalDeductibleAmount(primary_insurance, treatment_plan)
  )
}

/**
 * @type {(
 *  primary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  secondary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan
 * ) => number}
 */
export function getSecondaryInsuranceMaximumReimbursementAmount(
  primary_insurance,
  secondary_insurance,
  payment_plan,
  treatment_plan
) {
  let primary_insurance_reimbursement =
    primary_insurance && primary_insurance.insured_id
      ? getPrimaryInsuranceMaximumReimbursementAmount(
          primary_insurance,
          payment_plan,
          treatment_plan
        )
      : 0
  let total_deductible = getTotalDeductibleAmount(
    secondary_insurance,
    treatment_plan
  )
  let treatment_fee = getInsuranceTreatmentFee(payment_plan)
  if (
    secondary_insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages.cob_type ===
    COORDINATION_OF_BENEFITS_TYPE.NON_DUPLICATING
  ) {
    // calculate the reimbursement amount as if it was the primary insurance
    // and then it will only cover the difference, if it was to cover more than the primary insurance covers
    let maximum_reimbursement = getMaximumReimbursementAmount(
      secondary_insurance,
      treatment_fee,
      total_deductible
    )
    return Math.max(
      BigNumber(maximum_reimbursement)
        .minus(primary_insurance_reimbursement)
        .decimalPlaces(2)
        .toNumber(),
      0
    )
  } else {
    return Math.min(
      getMaximumReimbursementAmount(
        secondary_insurance,
        treatment_fee,
        total_deductible
      ),
      BigNumber(treatment_fee)
        .minus(primary_insurance_reimbursement)
        .decimalPlaces(2)
        .toNumber()
    )
  }
}

/**
 * @type {(
 *  insurance: import('./payment-plan-types.d.ts').Insurance,
 *  treatment_fee: number,
 *  total_deductible: number
 * ) => number}
 */
function getMaximumReimbursementAmount(
  insurance,
  treatment_fee,
  total_deductible
) {
  let maximum_reimbursement = BigNumber(treatment_fee)
    .multipliedBy(
      insurance.insured.insurance_subscription.insurance_plan
        .tx_category_coverages.ortho_coverages.reimbursement_percentage
    )
    .minus(total_deductible)
    .decimalPlaces(2)
    .toNumber()
  let remaining_coverage = BigNumber(
    insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages.max_lifetime_coverage
  )
    .minus(
      insurance.insured.tx_category_insured.ortho_insured.used_lifetime_coverage
    )
    .decimalPlaces(2)
    .toNumber()

  // making sure it won't end up with a negative value
  // and that it doesn't go over the total treatment fee
  return Math.min(
    Math.max(Math.min(remaining_coverage, maximum_reimbursement), 0),
    treatment_fee
  )
}

/**
 * @type {(
 *  insurance: import('./payment-plan-types.d.ts').Insurance,
 *  treatment_fee: number,
 *  maximum_reimbursement_amount: number
 * ) => number}
 */
export function getInsuranceDownpayment(
  insurance,
  treatment_fee,
  maximum_reimbursement_amount
) {
  let ortho_coverage =
    insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages

  let result = 0
  if (ortho_coverage.downpayment_type === DOWNPAYMENT_TYPE.FIXED) {
    result = ortho_coverage.downpayment_amount
  } else {
    switch (ortho_coverage.downpayment_percentage_base) {
      case DOWNPAYMENT_PERCENTAGE_BASE_TYPE.LIFETIME_COVERAGE: {
        result = BigNumber(ortho_coverage.max_lifetime_coverage)
          .multipliedBy(ortho_coverage.downpayment_percentage)
          .decimalPlaces(2)
          .toNumber()

        break
      }
      case DOWNPAYMENT_PERCENTAGE_BASE_TYPE.TX_FEE: {
        result = BigNumber(treatment_fee)
          .multipliedBy(ortho_coverage.reimbursement_percentage)
          .multipliedBy(ortho_coverage.downpayment_percentage)
          .decimalPlaces(2)
          .toNumber()
        break
      }
      default: {
        break
      }
    }
  }

  return Math.min(result, maximum_reimbursement_amount)
}

/**
 * @type {(
 *  insurance: import('./payment-plan-types.d.ts').Insurance,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  maximum_reimbursement_amount: number,
 *  downpayment_amount: number
 * ) => number}
 */
export function getInsuranceInstallmentAmount(
  insurance,
  treatment_plan,
  maximum_reimbursement_amount,
  downpayment_amount
) {
  let start_date = new Date()
  let end_date = addWeeks(
    start_date,
    treatment_plan.length_in_weeks ?? DEFAULT_TREATMENT_PLAN_LENGTH_IN_WEEKS
  )
  let length_in_months = differenceInCalendarMonths(end_date, start_date)
  if (length_in_months === 0) return 0

  return BigNumber(maximum_reimbursement_amount)
    .minus(downpayment_amount)
    .dividedBy(length_in_months)
    .multipliedBy(
      getNumberOfMonthsInInterval(
        insurance.insured.insurance_subscription.insurance_plan
          .insurance_company.installment_interval
      )
    )
    .decimalPlaces(2)
    .toNumber()
}

/**
 * @type {(
 *  primary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan
 * ) => {
 *  estimated_reimbursement_amount: number,
 *  downpayment_amount: number,
 *  installment_amount: number,
 *  loss_due_to_effective_date: number,
 *  loss_due_to_age_limit: number
 * }}
 */
export function getPrimaryInsuranceEstimatedReimbursement(
  primary_insurance,
  payment_plan,
  treatment_plan
) {
  return getEstimatedReimbursementAmount(
    primary_insurance,
    treatment_plan,
    getPrimaryInsuranceMaximumReimbursementAmount(
      primary_insurance,
      payment_plan,
      treatment_plan
    ),
    getInsuranceTreatmentFee(payment_plan)
  )
}

/**
 * @type {(
 *  primary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  secondary_insurance: import('./payment-plan-types.d.ts').Insurance,
 *  payment_plan: import('./payment-plan-types.d.ts').PaymentPlan,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan
 * ) => {
 *  estimated_reimbursement_amount: number,
 *  downpayment_amount: number,
 *  installment_amount: number,
 *  loss_due_to_effective_date: number,
 *  loss_due_to_age_limit: number
 * }}
 */
export function getSecondaryInsuranceEstimatedReimbursement(
  primary_insurance,
  secondary_insurance,
  payment_plan,
  treatment_plan
) {
  // allow selecting a secondary insurance while the primary insurance is a placeholder
  let primary_insurance_estimated_reimbursement_amount =
    primary_insurance?.estimated_reimbursement_amount ?? 0

  let maximum_reimbursement_amount =
    getSecondaryInsuranceMaximumReimbursementAmount(
      primary_insurance,
      secondary_insurance,
      payment_plan,
      treatment_plan
    )

  let treatment_fee = getInsuranceTreatmentFee(payment_plan)
  let result = getEstimatedReimbursementAmount(
    secondary_insurance,
    treatment_plan,
    maximum_reimbursement_amount,
    treatment_fee
  )

  return {
    estimated_reimbursement_amount: Math.min(
      result.estimated_reimbursement_amount,
      BigNumber(treatment_fee)
        .minus(primary_insurance_estimated_reimbursement_amount)
        .decimalPlaces(2)
        .toNumber()
    ),
    downpayment_amount: result.downpayment_amount,
    installment_amount: result.installment_amount,
    loss_due_to_effective_date: result.loss_due_to_effective_date,
    loss_due_to_age_limit: result.loss_due_to_age_limit,
  }
}

/**
 * @type {(
 *  insurance: import('./payment-plan-types.d.ts').Insurance,
 *  treatment_plan: import('./payment-plan-types.d.ts').TreatmentPlan,
 *  maximum_reimbursement_amount: number,
 *  treatment_fee: number
 * ) => {
 *  estimated_reimbursement_amount: number,
 *  downpayment_amount: number,
 *  installment_amount: number,
 *  loss_due_to_effective_date: number,
 *  loss_due_to_age_limit: number
 * }}
 */
function getEstimatedReimbursementAmount(
  insurance,
  treatment_plan,
  maximum_reimbursement_amount,
  treatment_fee
) {
  let downpayment_amount = getInsuranceDownpayment(
    insurance,
    treatment_fee,
    maximum_reimbursement_amount
  )
  let installment_amount = getInsuranceInstallmentAmount(
    insurance,
    treatment_plan,
    maximum_reimbursement_amount,
    downpayment_amount
  )

  let start_date = new Date()
  let end_date = addWeeks(
    start_date,
    treatment_plan.length_in_weeks ?? DEFAULT_TREATMENT_PLAN_LENGTH_IN_WEEKS
  )
  let date = max([getEffectiveStartDate(insurance) ?? start_date, start_date])

  let remaining = maximum_reimbursement_amount
  let loss_due_to_age_limit = 0
  let last_round = false
  while (!last_round) {
    last_round = isEqual(date, end_date) || isAfter(date, end_date)

    let payment_amount = isEqual(date, start_date)
      ? Math.min(downpayment_amount, remaining)
      : Math.min(installment_amount, remaining)
    let coverage_end_date = getCoverageEndDate(insurance)
    if (coverage_end_date && isAfter(date, coverage_end_date)) {
      loss_due_to_age_limit = BigNumber(loss_due_to_age_limit)
        .plus(payment_amount)
        .decimalPlaces(2)
        .toNumber()
      payment_amount = 0
    }

    remaining = BigNumber(remaining)
      .minus(payment_amount)
      .decimalPlaces(2)
      .toNumber()
    date = min([
      addMonths(
        date,
        getNumberOfMonthsInInterval(
          insurance.insured.insurance_subscription.insurance_plan
            .insurance_company.installment_interval
        )
      ),
      end_date,
    ])
  }

  let loss_due_to_effective_date = BigNumber(remaining)
    .minus(loss_due_to_age_limit)
    .decimalPlaces(2)
    .toNumber()
  let estimated_reimbursement_amount = insurance.is_overridden
    ? insurance.estimated_reimbursement_amount
    : Math.max(
        BigNumber(maximum_reimbursement_amount)
          .minus(remaining)
          .decimalPlaces(2)
          .toNumber(),
        0
      )

  if (downpayment_amount > estimated_reimbursement_amount) {
    downpayment_amount = estimated_reimbursement_amount
    installment_amount = 0
  }

  return {
    estimated_reimbursement_amount,
    downpayment_amount,
    installment_amount,
    loss_due_to_effective_date,
    loss_due_to_age_limit,
  }
}

/** @type {(insurance: import('./payment-plan-types.d.ts').Insurance) => Date | null} */
export function getEffectiveStartDate(insurance) {
  let ortho_coverage =
    insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages
  if (!insurance.insured.insurance_subscription.enrollment_date) {
    return null
  }

  return addDays(
    new Date(insurance.insured.insurance_subscription.enrollment_date),
    ortho_coverage.waiting_period ?? 0
  )
}

/** @type {(insurance: import('./payment-plan-types.d.ts').Insurance) => Date | null} */
function getCoverageEndDate(insurance) {
  let ortho_coverage =
    insurance.insured.insurance_subscription.insurance_plan
      .tx_category_coverages.ortho_coverages
  if (
    insurance.insured.insurance_subscription.person.id ===
      insurance.insured.patient.person.id ||
    !insurance.insured.patient.person.birth_date ||
    !ortho_coverage.dependent_age_limit ||
    !ortho_coverage.age_limit_cutoff
  ) {
    return null
  }
  let coverage_end_date = addYears(
    new Date(insurance.insured.patient.person.birth_date),
    ortho_coverage.dependent_age_limit
  )
  switch (ortho_coverage.age_limit_cutoff) {
    case AGE_LIMIT_CUTOFF.END_OF_MONTH:
      return endOfMonth(coverage_end_date)
    case AGE_LIMIT_CUTOFF.END_OF_YEAR:
      return endOfYear(coverage_end_date)
    case AGE_LIMIT_CUTOFF.IMMEDIATE:
    default:
      return coverage_end_date
  }
}

/** @type {(interval: keyof typeof INSURANCE_INSTALLMENT_INTERVAL) => number} */
function getNumberOfMonthsInInterval(interval) {
  switch (interval) {
    case INSURANCE_INSTALLMENT_INTERVAL.QUARTERLY:
      return 3
    case INSURANCE_INSTALLMENT_INTERVAL.YEARLY:
      return 12
    case INSURANCE_INSTALLMENT_INTERVAL.MONTHLY:
    default:
      return 1
  }
}

/**
 * @type {(
 *  a: import('./payment-plan-types.d.ts').Insurance['insured'],
 *  b: import('./payment-plan-types.d.ts').Insurance['insured']
 * ) => number}
 */
export function getSortedInsurances(a, b) {
  let a_birth_date = a.insurance_subscription?.person?.birth_date
  let b_birth_date = b.insurance_subscription?.person?.birth_date

  if (!a_birth_date && !b_birth_date) return 0
  if (!a_birth_date) return 1
  if (!b_birth_date) return -1

  // set the same year - comparing the day an month only
  return compareAsc(
    setYear(parseISO(a_birth_date), 2016),
    setYear(parseISO(b_birth_date), 2016)
  )
}

/**
 * Calculates the percentage of a value relative to a total.
 *
 * @param {number} part - The part value (numerator).
 * @param {number} total - The total value (denominator).
 * @returns
 */
export function getPercentage(part, total) {
  if (total > 0) {
    return BigNumber(part)
      .dividedBy(total)
      .multipliedBy(100)
      .decimalPlaces(2)
      .toNumber()
  } else {
    return 0
  }
}

/**
 * IMPORTANT: This function changes the "next" param and is expected to be used in a useDataChange context.
 *
 * @param {*} next param of the change function on payment_plan data context (mutable)
 */
export function changeDiscountsAppliedToTreatmentFee(next) {
  // update the amount of all the discounts applied to treatment's fee
  next.discounts
    .filter(discount => !discount.is_applied_to_payor)
    .forEach(discount => {
      if (discount.type === DISCOUNT_TYPE.FIXED) {
        discount.percentage = getPercentage(discount.amount, next.treatment_fee)
      } else {
        discount.amount = BigNumber(next.treatment_fee)
          .multipliedBy(discount.percentage)
          .dividedBy(100)
          .decimalPlaces(2)
          .toNumber()
      }
    })
}

/**
 * IMPORTANT: This function changes the "next" param and is expected to be used in a useDataChange context.
 *
 * Updates all insurances' estimated reimbursement amount when the treatment fee, discounts or charges changed.
 *
 * @param {*} next param of the change function on payment_plan data context (mutable)
 * @param {import('./payment-plan-types.d.ts').TreatmentPlan} treatment_plan
 * @returns
 */
export function changeInsurancesEstimatedReimbursementAmount(
  next,
  treatment_plan
) {
  // recalculate insurances' estimated reimbursement amount
  // make sure it's first recalculates the value for primary insuranse since secondary insurance depends on it
  let primary_insurance = next.insurances.find(item => item.is_primary)
  if (
    primary_insurance &&
    primary_insurance.insured_id &&
    // estimated reimbursement overridden by the user, keeping the selection
    !primary_insurance.is_overridden
  ) {
    let {
      estimated_reimbursement_amount:
        primary_insurance_estimated_reimbursement_amount,
      downpayment_amount: primary_insurance_downpayment_amount,
      installment_amount: primary_insurance_installment_amount,
    } = getPrimaryInsuranceEstimatedReimbursement(
      primary_insurance,
      next,
      treatment_plan
    )
    primary_insurance.estimated_reimbursement_amount =
      primary_insurance_estimated_reimbursement_amount
    primary_insurance.downpayment_amount = primary_insurance_downpayment_amount
    primary_insurance.installment_amount = primary_insurance_installment_amount
  }

  let secondary_insurance = next.insurances.find(item => !item.is_primary)
  if (
    secondary_insurance &&
    secondary_insurance.insured_id &&
    !secondary_insurance.is_overridden
  ) {
    let {
      estimated_reimbursement_amount:
        secondary_insurance_estimated_reimbursement_amount,
      downpayment_amount: secondary_insurance_downpayment_amount,
      installment_amount: secondary_insurance_installment_amount,
    } = getSecondaryInsuranceEstimatedReimbursement(
      primary_insurance,
      secondary_insurance,
      next,
      treatment_plan
    )
    secondary_insurance.estimated_reimbursement_amount =
      secondary_insurance_estimated_reimbursement_amount
    secondary_insurance.downpayment_amount =
      secondary_insurance_downpayment_amount
    secondary_insurance.installment_amount =
      secondary_insurance_installment_amount
  }
}

/**
 * IMPORTANT: This function changes the "next" param and is expected to be used in a useDataChange context.
 *
 * Updates all payors' share to reflect the percentage of the remaining fee after applying the discounts, charges and insurance to be paid by each payor.
 *
 * @param {*} next param of the change function on payment_plan data context (mutable)
 */
export function changePayorsShare(next) {
  // update payor's share to take into account the current discount amount
  let treatment_fee = getAdjustedTreatmentFeeAfterInsurances(next)
  next.payors.forEach(payor => {
    payor.share.amount = BigNumber(treatment_fee)
      .multipliedBy(payor.share.percentage)
      .dividedBy(100)
      .decimalPlaces(2)
      .toNumber()
  })
}

/**
 * IMPORTANT: This function changes the "next" param and is expected to be used in a useDataChange context.
 *
 * Updates all the discounts applied to payor's share when the base for calculation changed.
 *
 * @param {*} next param of the change function on payment_plan data context (mutable)
 */
export function changeDiscountsAppliedToPayorsShare(next) {
  // all the payor's discounts should be recalculated to use the updated adjusted treatment fee
  // update discounts' amount to be applied on the new payor's share
  next.discounts
    .filter(discount => discount.is_applied_to_payor)
    .forEach(discount => {
      let payor = getPayorForDiscount(discount, next)
      if (!payor) return

      if (discount.type === DISCOUNT_TYPE.FIXED) {
        discount.percentage = getPercentage(discount.amount, payor.share.amount)
      } else {
        discount.amount = BigNumber(payor.share.amount)
          .multipliedBy(discount.percentage)
          .dividedBy(100)
          .decimalPlaces(2)
          .toNumber()
      }
    })
}

/**
 * IMPORTANT: This function changes the "next" param and is expected to be used in a useDataChange context.
 *
 * Update payors' downpayment and installment amount when payor's share or discounts changed.
 *
 * @param {*} next param of the change function on payment_plan data context (mutable)
 */
export function changePayorsConfiguration(next) {
  next.payors.forEach(payor => {
    let config = getUpdatedPayorConfiguration(payor, next)
    payor.downpayment_amount = config.downpayment_amount
    payor.installment_amount = config.installment_amount
    payor.payment_plan_length = config.payment_plan_length
  })
}
